import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { BadgeDollarSign, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, brl, fmtDate } from "@/lib/api";

function beep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [880, 1320].forEach((f, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.frequency.value = f;
      g.gain.setValueAtTime(0.15, ctx.currentTime + i * 0.18);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.18 + 0.16);
      o.connect(g).connect(ctx.destination);
      o.start(ctx.currentTime + i * 0.18);
      o.stop(ctx.currentTime + i * 0.18 + 0.17);
    });
  } catch {
    /* audio blocked by browser */
  }
}

export function PaymentAlerts({ onOpenOrders }) {
  const [alerts, setAlerts] = useState([]);
  const seen = useRef(null);

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/payment-alerts");
      if (seen.current) {
        const fresh = data.filter((a) => !seen.current.has(a.id));
        if (fresh.length) {
          beep();
          fresh.forEach((a) => toast.success(`Novo pedido pago: #${a.number} · ${brl(a.total)}`, { duration: 10000 }));
        }
      }
      seen.current = new Set(data.map((a) => a.id));
      setAlerts(data);
    } catch {
      /* ignore polling errors */
    }
  }, []);

  useEffect(() => {
    poll();
    const t = setInterval(poll, 30000);
    return () => clearInterval(t);
  }, [poll]);

  const ack = async () => {
    await api.post("/admin/payment-alerts/ack", { ids: alerts.map((a) => a.id) }).catch(() => {});
    setAlerts([]);
  };

  if (!alerts.length) return null;
  return (
    <div className="mb-6 rounded-md border-2 border-green-400 bg-green-50 p-4" data-testid="payment-alerts-banner">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 font-display font-bold text-green-900">
          <BadgeDollarSign className="h-5 w-5" />
          {alerts.length === 1 ? "1 novo pedido pago" : `${alerts.length} novos pedidos pagos`}
        </div>
        <button onClick={ack} aria-label="Fechar" data-testid="payment-alerts-close"><X className="h-4 w-4 text-green-800" /></button>
      </div>
      <ul className="mt-2 space-y-1 text-sm text-green-900" data-testid="payment-alerts-list">
        {alerts.map((a) => (
          <li key={a.id} data-testid={`payment-alert-${a.number}`}>
            <span className="font-mono font-bold">#{a.number}</span> · {a.customer?.name} · {brl(a.total)} · {fmtDate(a.paid_at)}
          </li>
        ))}
      </ul>
      <div className="mt-3 flex gap-2">
        <Button size="sm" onClick={() => { ack(); onOpenOrders(); }} className="bg-green-700 hover:bg-green-800" data-testid="payment-alerts-view">Ver pedidos</Button>
        <Button size="sm" variant="outline" onClick={ack} data-testid="payment-alerts-ack">Ok, visto</Button>
      </div>
    </div>
  );
}
